import { useMediaQuery } from "@mantine/hooks";

/**
 * MUI-era responsive value ({ xs: …, md: … }), resolved against the legacy
 * MUI breakpoints (sm 600 / md 960 / lg 1440 / xl 1920). Transitional helper
 * for the MUI → Mantine migration — new code should use Mantine responsive
 * props instead.
 */
export type MuiResponsiveValue<T> =
  | T
  | { xs?: T; sm?: T; md?: T; lg?: T; xl?: T };

const BREAKPOINT_ORDER = ["xs", "sm", "md", "lg", "xl"] as const;

export const useMuiBreakpointIndex = () => {
  const options = { getInitialValueInEffect: false } as const;
  const sm = useMediaQuery("(min-width: 37.5em)", true, options);
  const md = useMediaQuery("(min-width: 60em)", true, options);
  const lg = useMediaQuery("(min-width: 90em)", true, options);
  const xl = useMediaQuery("(min-width: 120em)", true, options);
  return xl ? 4 : lg ? 3 : md ? 2 : sm ? 1 : 0;
};

export function useMuiResponsiveValue<T>(
  value: MuiResponsiveValue<T> | undefined,
): T | undefined {
  const index = useMuiBreakpointIndex();
  if (value === undefined) return undefined;
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    const record = value as { xs?: T; sm?: T; md?: T; lg?: T; xl?: T };
    if (BREAKPOINT_ORDER.some((key) => key in record)) {
      // MUI semantics: a breakpoint's value applies from it upwards, so take
      // the closest defined value at or below the current breakpoint
      for (let i = index; i >= 0; i--) {
        const candidate = record[BREAKPOINT_ORDER[i]];
        if (candidate !== undefined) return candidate;
      }
      return undefined;
    }
  }
  return value as T;
}
